import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { alertError, alertSuccess } from '../../components/Toast';
import { sanitizeCnpj, isValidCnpj } from '../../core/utils/cnpj';
import ModalCompany from '../../screens/ModalCompany/ModalCompany';
import { supabase } from '../../supabaseClient';
import { setData } from '../../core/redux/app/Actions';
import { swalInput, swalRemove } from '../../components/Swal/Swal';
import { compareValues } from '../ModalClients/ModalClientsContainer';
import Swal from 'sweetalert2';
import ModalAddUser from '../../components/ModalAddUser/ModalAddUser';
import { seedDefaultAccountPlan } from '../../core/utils/accountPlan';
import { callAdminUsers } from '../../core/utils/adminUsers';

const ModalCompanyContainer = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const id = location.pathname.split('/')[3];
  const params = useParams();
  const isNew = params.id === '0';
  const [values, setValues] = useState({ active: true });
  const [oldValues, setOldValues] = useState({ active: true });
  const [companyList, setCompanyList] = useState({});
  const [users, setUsers] = useState([]);
  const [reloadUsers, setReloadUsers] = useState(false);
  const [consultants, setConsultants] = useState([]);
  const [contractHistory, setContractHistory] = useState([]);
  const [showModalAddUser, setShowModalAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const reloadCompany = useSelector(
    (state) => state.app.reloadCompany || false
  );
  const loggedUser = useSelector((state) => state.app.user || {});

  const loadData = async () => {
    let { data, error } = await supabase
      .from('Company')
      .select('*')
      .eq('id', id)
      .is('deleted_at', null);

    const { data: checklist, error: checklistError } = await supabase
      .from('CompanyChecklist')
      .select('id, name')
      .eq('company_id', id);

    const { data: history, error: historyError } = await supabase
      .from('ContractHistory')
      .select('*')
      .eq('company_id', id)
      .is('deleted_at', null)
      .order('start_date', { ascending: false });

    if (!checklistError && checklist) {
      data[0].checklist = checklist;
    }
    if (!historyError && history) {
      setContractHistory(history);
    }
    if (!error && data && data.length > 0) {
      const companyData = { ...data[0], checklist: checklist || [] };
      setValues(companyData);
      setOldValues(companyData);
    }
  };

  const loadCompanyList = async () => {
    let { data, error } = await supabase
      .from('Company')
      .select('*')
      .is('deleted_at', null);

    if (!error) {
      setCompanyList(
        data.map((item) => ({ label: item.name, value: item.id }))
      );
    } else {
      console.log(error);
    }
  };

  useEffect(() => {
    if (id !== '0') {
      loadData();
      loadUsers();
      loadConsultants();
    }
  }, [id]);

  useEffect(() => {
    loadCompanyList();
  }, []);

  useEffect(() => {
    const onBackButtonEvent = (e) => {
      e.preventDefault();
      closeModal();
    };

    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener('popstate', onBackButtonEvent);
    return () => {
      window.removeEventListener('popstate', onBackButtonEvent);
    };
  }, []); // Removido dependência de values - closeModal já tem acesso ao values atual via closure

  // Usar useMemo para evitar comparações desnecessárias
  const hasChanges = useMemo(() => {
    return compareValues(oldValues, values);
  }, [JSON.stringify(values), JSON.stringify(oldValues)]);

  useEffect(() => {
    const path = location.pathname.split('/')[1];
    dispatch(
      setData({ [`compareValues_${path}`]: hasChanges })
    );
  }, [hasChanges, dispatch, location.pathname]);

  useEffect(() => {
    loadUsers();
  }, [reloadUsers]);

  const loadUsers = async () => {
    const { data, error } = await supabase
      .from('UserCompany')
      .select(
        `
                type,
                user (
                    *
                )`
      )
      .eq('company', params.id)
      .is('deleted_at', null);

    if (!error) {
      const newData = data.map(({ user, type }) => ({ ...user, type }));
      setUsers(newData);
    }
  };

  const loadConsultants = async () => {
    const { data, error } = await supabase
      .from('CompanyConsultant')
      .select('id, name')
      .eq('company_id', params.id)
      .is('deleted_at', null);
    if (!error) {
      setConsultants(data);
    }
  };

  const handleSaveContractPeriod = async (period) => {
    const dataToSave = {
      ...period,
      company_id: params.id,
      created_by: loggedUser?.id
    };

    const { data, error } = await supabase
      .from('ContractHistory')
      .insert([dataToSave])
      .select('*');

    if (!error && data) {
      setContractHistory((prev) => [data[0], ...prev]);
      alertSuccess('Período adicionado com sucesso!');
      return data[0];
    } else {
      alertError('Erro ao salvar período');
      return null;
    }
  };

  const handleRemoveContractPeriod = async (periodId) => {
    if (periodId) {
      const { error } = await supabase
        .from('ContractHistory')
        .update({ deleted_at: new Date(), deleted_by: loggedUser?.id })
        .eq('id', periodId);

      if (!error) {
        setContractHistory((prev) => prev.filter(item => item.id !== periodId));
        alertSuccess('Período removido com sucesso!');
      } else {
        alertError('Erro ao remover período');
      }
    }
  };

  const closeModal = () => {
    if (compareValues(oldValues, values)) {
      Swal.fire({
        title: 'Deseja sair sem salvar?',
        showCancelButton: true,
        showConfirmButton: false,
        showDenyButton: true,
        icon: 'warning',
        text: 'Ao sair, seus dados serão descartados.',
        denyButtonText: `Sair`,
        cancelButtonText: 'Cancelar',
      }).then((result) => {
        if (result.isDenied) {
          history.push('/company');
        }
      });
    } else {
      history.push('/company');
    }
  };


  const addUserWithPassword = async (userData) => {
    if (isLoadingUser) return; // Previne duplo clique

    setIsLoadingUser(true);
    try {
      const { email, password, name, type } = userData;

      // Verifica se o e-mail já existe na empresa atual
      const existingUser = users.find((item) => item.email === email);
      if (existingUser) {
        alertError('Este e-mail já está cadastrado nesta empresa');
        return;
      }

      const { error } = await callAdminUsers('create-user', {
        targetCompanyId: params.id,
        email,
        name,
        type,
        password,
      });

      if (error) {
        if (error.message === 'already-linked') {
          alertError('Este usuário já está vinculado a esta empresa');
        } else {
          alertError('Erro ao cadastrar usuário. Tente novamente mais tarde.');
        }
        return;
      }

      // Recarrega a lista de usuários
      await loadUsers();

      alertSuccess('Usuário cadastrado com sucesso! Ele já pode acessar o sistema.');
      setShowModalAddUser(false);

    } catch (error) {
      console.error('Erro ao adicionar usuário:', error);
      alertError('Erro inesperado ao cadastrar usuário. Tente novamente mais tarde.');
    } finally {
      setIsLoadingUser(false);
    }
  };

  const updateUserData = async (userData) => {
    if (isLoadingUser) return; // Previne duplo clique

    setIsLoadingUser(true);
    try {
      const { id, email, password, name, type } = userData;

      const { error } = await callAdminUsers('update-user', {
        userId: id,
        targetCompanyId: params.id,
        email,
        name,
        type,
        password: password || undefined,
      });

      if (error) {
        alertError('Erro ao atualizar usuário. Tente novamente mais tarde.');
        return;
      }

      // Recarrega a lista de usuários
      await loadUsers();

      alertSuccess('Usuário atualizado com sucesso!');
      setShowModalAddUser(false);
      setEditingUser(null);

    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      alertError('Erro inesperado ao atualizar usuário. Tente novamente mais tarde.');
    } finally {
      setIsLoadingUser(false);
    }
  };

  const handleSaveUser = (userData) => {
    if (userData.id) {
      // Modo de edição
      updateUserData(userData);
    } else {
      // Modo de criação
      addUserWithPassword(userData);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowModalAddUser(true);
  };

  const addCompanyConsultant = async (name) => {
    if (name && name !== '') {
      if (!users.find((item) => item.name === name)) {
        const user = { name, id: null };
        const { data, error } = await supabase
          .from('CompanyConsultant')
          .insert([{ name, company_id: params.id }])
          .select(`*`);
        alertSuccess('Sucesso ao salvar o consultor!');
        setConsultants((previous) => previous.concat(data[0]));
      } else {
        alertError('Nome já existe nessa empresa');
      }
    } else {
      alertError('Nome é inválido');
    }
  };

  const onChange = (field, value) => {
    setValues((values) => ({ ...values, [field]: value }));
  };

  const copyUsers = async (sourceCompanyId, targetCompanyId) => {
    const { data, error } = await supabase
      .from('Client')
      .select('*')
      .eq('id_company', sourceCompanyId)
      .is('deleted_at', null);

    if (!error) {
      const usersToCopy = data.map(({ id, ...rest }) => ({
        ...rest,
        id_company: targetCompanyId,
      }));

      const { data: newData, error: insertError } = await supabase
        .from('Client')
        .insert(usersToCopy);

      if (insertError) {
        console.error('Erro ao copiar usuários:', insertError);
      }
    } else {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const copyConfig = async (sourceCompanyId, targetCompanyId) => {
    const newParts = await supabase
      .from('Part')
      .select('*')
      .eq('company', sourceCompanyId)
      .is('deleted_at', null);

    if (!newParts.error) {
      const dataToCopy = newParts.data.map(({ id, ...rest }) => ({
        ...rest,
        company: targetCompanyId,
      }));
      const { data: newData, error: insertError } = await supabase
        .from('Part')
        .insert(dataToCopy);
      if (insertError) {
        console.error(`Erro ao copiar dados da tabela Part:`, insertError);
      }
    } else {
      console.error(`Erro ao carregar dados da tabela Part:`, newParts.error);
    }

    const newServiceTypes = await supabase
      .from('ServiceTypes')
      .select('*')
      .eq('id_company', sourceCompanyId)
      .is('deleted_at', null);
    let newServiceTypesData = [];
    if (!newServiceTypes.error) {
      const dataToCopy = newServiceTypes.data.map(({ id, ...rest }) => ({
        ...rest,
        id_company: targetCompanyId,
      }));
      const { data: newData, error: insertError } = await supabase
        .from('ServiceTypes')
        .insert(dataToCopy);
      newServiceTypesData = newData;
      if (insertError) {
        console.error(
          `Erro ao copiar dados da tabela ServiceTypes:`,
          insertError
        );
      }
    } else {
      console.error(
        `Erro ao carregar dados da tabela ServiceTypes:`,
        newServiceTypes.error
      );
    }

    const allServiceTypes = await supabase
      .from('ServiceTypes')
      .select('id, title')
      .eq('id_company', sourceCompanyId)
      .is('deleted_at', null);

    const newServices = await supabase
      .from('Services')
      .select('*')
      .eq('id_company', sourceCompanyId)
      .is('deleted_at', null);

    if (!newServices.error) {
      const dataToCopy = newServices.data.map(
        ({ id, service_type, ...rest }) => {
          const oldService = allServiceTypes.data.find(
            (type) => type.id === service_type
          );
          var matchingServiceType = null;
          if (oldService) {
            matchingServiceType = newServiceTypesData.find(
              (item) => item.title === oldService.title
            );
          } else {
            matchingServiceType = newServiceTypesData.find((item) =>
              item.title.includes(extrairTextoEntreParenteses(rest.title))
            );
          }
          return {
            ...rest,
            id_company: targetCompanyId,
            service_type: matchingServiceType ? matchingServiceType.id : null,
          };
        }
      );

      const { data: newData, error: insertError } = await supabase
        .from('Services')
        .insert(dataToCopy);

      if (insertError) {
        console.error(`Erro ao copiar dados da tabela Services:`, insertError);
      }
    } else {
      console.error(
        `Erro ao carregar dados da tabela Services:`,
        newServices.error
      );
    }

    const newValueHour = await supabase
      .from('HourlyValue')
      .select('*')
      .eq('id_company', sourceCompanyId)
      .is('deleted_at', null);

    if (!newValueHour.error) {
      const dataToCopy = newValueHour.data.map(({ id, ...rest }) => {
        return {
          ...rest,
          id_company: targetCompanyId,
          service_types: JSON.stringify(
            newServiceTypesData.map((item, index) => {
              const extraData = {};
              const value = JSON.parse(rest.service_types).find(
                (row, i) => i === index
              )?.value;
              if (value) {
                extraData['value'] = value;
              }
              return {
                ...item,
                ...extraData,
              };
            })
          ),
        };
      });

      const { data: newData, error: insertError } = await supabase
        .from('HourlyValue')
        .insert(dataToCopy);

      if (insertError) {
        console.error(
          `Erro ao copiar dados da tabela HourlyValue:`,
          insertError
        );
      }
    } else {
      console.error(
        `Erro ao carregar dados da tabela HourlyValue:`,
        newValueHour.error
      );
    }
  };

  const onSave = async () => {
    console.log('Values ao salvar:', values);

    if (!values?.name) {
      alertError('Nome é obrigatório');
      return;
    }

    if (!values?.cellphone || values.cellphone[14] === '_') {
      alertError('Celular é obrigatório');
      return;
    }

    if (!values?.cnpj) {
      alertError('CPF ou CNPJ é obrigatório');
      return;
    }

    // Só valida dígito verificador quando é CNPJ (14 chars); CPF passa.
    if (sanitizeCnpj(values.cnpj).length === 14 && !isValidCnpj(values.cnpj)) {
      alertError('CNPJ inválido');
      return;
    }

    if (!values?.cep) {
      alertError('CEP é obrigatório');
      return;
    }

    if (!values?.state) {
      console.log('State está vazio:', values.state);
      alertError('Estado é obrigatório');
      return;
    }

    if (!values?.city) {
      console.log('City está vazio:', values.city);
      alertError('Cidade é obrigatória');
      return;
    }

    if (!values?.street) {
      console.log('Street está vazio:', values.street);
      alertError('Rua é obrigatória');
      return;
    }

    if (!values?.neighborhood) {
      console.log('Neighborhood está vazio:', values.neighborhood);
      alertError('Bairro é obrigatório');
      return;
    }

    const { data, error } = await supabase
      .from('Company')
      .upsert([{ ...values, checklist: null }]);

    if (!error) {
      const company = data[0];
      const companyId = company.id;
      // dispatch(setData({ company }));

      // Empresa nova ganha o Plano de Contas padrão automaticamente.
      // Falha aqui não bloqueia a criação da empresa — fica registrada no
      // console e pode ser coberta depois pelo backfill SQL.
      if (isNew) {
        const seedResult = await seedDefaultAccountPlan({
          companyId,
          userId: loggedUser?.id,
        });
        if (seedResult.error) {
          console.error('Erro ao semear Plano de Contas padrão:', seedResult.error);
        }
      }

      const selectedCompanyId = values.selectedCompany
        ? values.selectedCompany.value
        : null;

      if (selectedCompanyId) {
        if (values.copyUsers) {
          await copyUsers(selectedCompanyId, companyId);
        }
        if (values.copyConfig) {
          await copyConfig(selectedCompanyId, companyId);
        }
      }

      alertSuccess('Sucesso ao salvar a empresa!');
      dispatch(setData({ reloadCompany: !reloadCompany }));
      history.push(`/company/details/${companyId}`);

      const { data: existingChecklists, error: fetchError } = await supabase
        .from('CompanyChecklist')
        .select('id')
        .eq('company_id', companyId);

      if (fetchError) {
        console.error('Erro ao buscar checklists existentes:', fetchError);
        return;
      }

      const existingChecklistIds = existingChecklists.map((item) => item.id);
      const newChecklistIds = values.checklist
        .map((item) => item.id)
        .filter(Boolean);

      // 🔹 Checklists que precisam ser removidos (estão no banco, mas não na nova lista)
      const checklistIdsToRemove = existingChecklistIds.filter(
        (id) => !newChecklistIds.includes(id)
      );

      if (checklistIdsToRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from('CompanyChecklist')
          .delete()
          .in('id', checklistIdsToRemove);

        if (deleteError) {
          console.error('Erro ao remover checklists antigos:', deleteError);
          return;
        }
      }

      // 🔹 Filtra apenas os checklists que ainda NÃO existem no banco para inserção
      const newChecklists = values.checklist.filter(
        (item) => !existingChecklistIds.includes(item.id)
      );

      if (newChecklists.length > 0) {
        const checklistData = newChecklists.map((item) => ({
          ...item,
          company_id: companyId, // Garante que pertence à empresa
        }));

        const { error: checklistError } = await supabase
          .from('CompanyChecklist')
          .insert(checklistData); // Usa insert ao invés de upsert para evitar conflitos

        if (checklistError) {
          console.error('Erro ao salvar os checklists:', checklistError);
        }
      }

      if (
        users.filter((item) => !item.id) &&
        users.filter((item) => !item.id).length > 0
      ) {
        saveUsers(companyId);
      } else {
        setOldValues(values);
        dispatch(setData({ reloadCompany: !reloadCompany }));
      }
    } else {
      alertError('Erro ao salvar a empresa, tente novamente mais tarde.');
    }
  };

  const saveUsers = async (company) => {
    const usersToSave = users.filter((item) => !item.id);
    let saved = [];

    for (let i = 0; i < usersToSave.length; i++) {
      const user = usersToSave[i];
      const email = `${user.email}`.trim().toLowerCase();
      const { error } = await callAdminUsers('create-user', {
        targetCompanyId: company,
        email,
        name: user.name,
        type: 3,
      });

      if (!error) {
        saved.push(user);
      }
    }

    if (saved.length > 0) {
      alertSuccess('Sucesso ao salvar os funcionários!');
      history.push('/company');
      dispatch(setData({ reloadCompany: !reloadCompany }));
    } else {
      alertError('Erro ao salvar os funcionários, tente novamente mais tarde.');
    }
  };

  const onRemoveUser = async (user) => {
    if (user && user.id) {
      const { error } = await callAdminUsers('remove-user', {
        userId: user.id,
        targetCompanyId: params.id,
        email: user.email,
      });

      if (!error) {
        alertSuccess('Funcionário removido com sucesso!');
        setUsers((users) => users.filter((row) => row.id !== user.id));
      } else {
        alertError('Erro ao remover o funcionário, tente novamente mais tarde');
      }
    } else {
      alertError('Erro ao remover o funcionário, tente novamente mais tarde');
    }
  };

  const onRemoveConsultant = async (user) => {
    if (user && user.id) {
      const { data, error } = await supabase
        .from('CompanyConsultant')
        .update({ deleted_at: new Date() })
        .eq('id', user.id)
        .eq('company_id', params.id);

      if (!error) {
        alertSuccess('Consultor removido com sucesso!');
        setConsultants((localConsultant) =>
          localConsultant.filter((row) => row.id !== user.id)
        );
      } else {
        alertError('Erro ao remover o consultor, tente novamente mais tarde');
      }
    } else {
      alertError('Erro ao remover o consultor, tente novamente mais tarde');
    }
  };

  const onEditPassword = async (user) => {
    const response = await swalInput();
    try {
      if (response?.isConfirmed || response?.value) {
        const password = response.value;
        if (!new RegExp(`^.{${6},}$`).test(password)) {
          alertError('A senha precisa ter pelo menos 6 dígitos');
          return;
        }

        const { error } = await callAdminUsers('set-password', {
          targetCompanyId: params.id,
          email: user.email,
          password,
        });
        if (error) throw error;
        alertSuccess('Senha cadastrada com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao atualizar senha:', error);
      alertError('Erro ao atualizar a senha. Tente novamente.');
    }
  };

  const onChangeType = async (item) => {
    if (item && item.type !== 0) {
      const promoted = item.type - 1;
      const newType = promoted > 0 ? promoted : 3;
      const { error } = await callAdminUsers('update-user', {
        userId: item.id,
        targetCompanyId: params.id,
        type: newType,
      });
      if (!error) {
        setReloadUsers((reload) => !reload);
        alertSuccess('Tipo do funcionário alterado com sucesso!');
      } else {
        alertError(
          'Erro ao alterar o tipo do funcionário, tente novamente mais tarde'
        );
      }
    }
  };

  const onSendEmail = async (user) => {
    if (user && user.email && !user.isRegistered) {
      const { error } = await callAdminUsers('invite-user', {
        targetCompanyId: params.id,
        email: user.email,
      });

      if (!error) {
        alertSuccess('E-mail reenviado com sucesso!');
      } else {
        alertError(error.message || 'Erro ao reenviar o e-mail');
      }
    }
  };

  const changeCEP = (cep) => {
    onChange('cep', cep);
    if (cep && cep.length >= 9 && /^[0-9]{5}-?[0-9]{3}$/.test(cep)) {
      fetch(`https://viacep.com.br/ws/${cep.replace('-', '')}/json/`)
        .then((response) => response.json())
        .then((data) => {
          if (!data.erro) {
            const { uf, logradouro, localidade, bairro } = data;
            setValues((values) => ({
              ...values,
              cep,
              state: uf || values.state,
              street: logradouro || values.street,
              city: localidade || values.city,
              neighborhood: bairro || values.neighborhood,
            }));
          }
        })
        .catch((error) => {
          console.error('Erro ao buscar CEP:', error);
        });
    }
  };

  const onDeactivateCompany = async () => {
    const confirmation = await Swal.fire({
      title: 'Desativar Empresa?',
      showCancelButton: true,
      showConfirmButton: false,
      showDenyButton: true,
      icon: 'warning',
      html: `
        <p style="margin-bottom: 12px;">Esta ação irá <strong>desativar</strong> a empresa imediatamente.</p>
        <p style="margin-bottom: 12px;"><strong>Consequências:</strong></p>
        <ul style="text-align: left; margin-left: 20px;">
          <li>Todos os usuários perderão acesso ao sistema</li>
          <li>Não será possível fazer login até a reativação</li>
          <li>Os dados não serão perdidos</li>
          <li>A empresa pode ser reativada posteriormente</li>
        </ul>
      `,
      denyButtonText: 'Desativar Empresa',
      cancelButtonText: 'Cancelar',
    });

    if (confirmation.isDenied) {
      if (values && values.id) {
        const { data, error } = await supabase
          .from('Company')
          .update({
            active: false,
          })
          .eq('id', values.id);

        if (!error) {
          alertSuccess('Empresa desativada com sucesso!');
          // Atualiza o valor local
          setValues({ ...values, active: false });
          setOldValues({ ...values, active: false });
          dispatch(setData({ reloadCompany: !reloadCompany }));
        } else {
          alertError('Erro ao desativar a empresa, tente novamente mais tarde!');
        }
      } else {
        alertError('Erro ao desativar a empresa, tente novamente mais tarde!');
      }
    }
  };

  const onRemove = async () => {
    const confirmation = await Swal.fire({
      title: 'Deseja mesmo excluir?',
      showCancelButton: true,
      showConfirmButton: false,
      showDenyButton: true,
      icon: 'warning',
      text: 'Seus dados serão completamente perdidos!',
      denyButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
    });

    if (confirmation.isDenied) {
      if (values && values.id) {
        const { data, error } = await supabase
          .from('Company')
          .update({
            deleted_at: new Date(),
            deleted_by: loggedUser?.id,
            cnpj: null,
          })
          .eq('id', values.id);

        if (!error) {
          alertSuccess('Empresa removida com sucesso!');
          history.push('/company');
          dispatch(setData({ reloadCompany: !reloadCompany }));
        } else {
          alertError('Erro ao remover a empresa, tente novamente mais tarde!');
        }
      } else {
        alertError('Erro ao remover a empresa, tente novamente mais tarde!');
      }
    } else {
      history.push('/company');
    }
  };

  return (
    <>
      <ModalCompany
        companyList={companyList}
        compareValues={compareValues(oldValues, values)}
        closeModal={closeModal}
        addCompanyConsultant={addCompanyConsultant}
        onChange={onChange}
        onSave={onSave}
        values={values}
        isNew={isNew}
        users={users}
        consultants={consultants}
        contractHistory={contractHistory}
        onSaveContractPeriod={handleSaveContractPeriod}
        onRemoveContractPeriod={handleRemoveContractPeriod}
        onChangeType={onChangeType}
        onRemove={onRemove}
        onDeactivateCompany={onDeactivateCompany}
        onRemoveUser={(item) =>
          swalRemove({
            withError: false,
            functionConfirmRemove: () => onRemoveUser(item),
          })
        }
        onRemoveConsultant={onRemoveConsultant}
        onEditUser={handleEditUser}
        onSendEmail={onSendEmail}
        changeCEP={changeCEP}
        loggedUser={loggedUser}
        onOpenModalAddUser={() => {
          setEditingUser(null);
          setShowModalAddUser(true);
        }}
      />
      <ModalAddUser
        isOpen={showModalAddUser}
        onClose={() => {
          setShowModalAddUser(false);
          setEditingUser(null);
        }}
        onSave={handleSaveUser}
        existingEmails={users.map((u) => u.email.toLowerCase().trim())}
        editUser={editingUser}
        isLoading={isLoadingUser}
      />
    </>
  );
};

export default ModalCompanyContainer;

function extrairTextoEntreParenteses(texto) {
  const regex = /\(([^)]+)\)/;
  const resultado = regex.exec(texto);
  return resultado ? resultado[1] : null;
}
